import FormButtons from 'features/contracts/components/form-buttons/FormButtons'
import dayjs from 'dayjs'
import { ISO8601_DATE_FORMAT } from 'constants/constants'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { TerminateDataInputs, TerminateDataProps, TerminateSteps } from 'features/contracts/terminate/types'
import IndexesFormsInputs from 'components/indexes-form-inputs/IndexesFormInputs'
import styles from '../../../Contracts.module.scss'
import ConfirmationModal from 'pages/app/consumption/edit-meter-readings/components/confirmation-modal/ConfirmationModal'
import { ConfirmationModalProps } from 'pages/app/consumption/edit-meter-readings/components/confirmation-modal/types'
import { useStoreSelector } from 'hooks/store'
import { ContractFlowTrackingTypes } from 'types/tracking.ts'
import DocumentUpload from 'features/contracts/components/document-upload/DocumentUpload.tsx'
import { Form, Heading } from '@boltenergy-be/design-system'
import classNames from 'classnames'
import DateField from 'components/form-fields/date-field/DateField.tsx'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'

const MeterDetails = ({ setNextStep, terminateData }: TerminateDataProps) => {
  // Redux store
  const serviceContracts = useStoreSelector(selectCurrentServiceContracts)

  // Local state
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false)
  const [confirmationState, setConfirmationState] = useState<ConfirmationModalProps['state']>('LOW')
  const [hasVeryHighUsage, setHasVeryHighUsage] = useState<boolean>(false)
  const [hasVeryLowUsage, setHasVeryLowUsage] = useState<boolean>(false)

  // i18n
  const { t } = useTranslation(['contracts', 'validation'])

  // React Hook Form
  const reactHookForm = useForm<TerminateDataInputs>({
    defaultValues: {
      ...terminateData[TerminateSteps.METER_DETAILS],
      docUpload: terminateData[TerminateSteps.METER_DETAILS].docUpload ?? null
    },
    mode: 'onChange'
  })
  const { handleSubmit, watch, getValues } = reactHookForm
  const watchDate = watch('date')

  // Constants
  const isDateInFuture = dayjs(watchDate).isAfter(dayjs())
  const minDate = dayjs().subtract(25, 'day').format(ISO8601_DATE_FORMAT)
  const maxDate = terminateData[TerminateSteps.METER_DETAILS].digitalMeter
    ? dayjs().add(180, 'day').format(ISO8601_DATE_FORMAT)
    : dayjs().format(ISO8601_DATE_FORMAT)

  /**
   * Handles the form submit
   * @param {TerminateDataInputs} data
   */
  const onSubmit = (data: TerminateDataInputs) => {
    if (hasVeryLowUsage || hasVeryHighUsage) {
      setConfirmationState(hasVeryHighUsage && hasVeryLowUsage ? 'BOTH' : hasVeryLowUsage ? 'LOW' : 'HIGH')
      setIsConfirmationModalOpen(true)
    } else {
      setNextStep(data, TerminateSteps.REFERRAL)
    }
  }

  return (
    <section className={styles['terminate-section']}>
      <Heading as="h1" variant="h4">
        {t(($) => $.terminate.meterDetails.title)}
      </Heading>
      <FormProvider {...reactHookForm}>
        <Form className={styles['narrow-grid']} onSubmit={handleSubmit(onSubmit)}>
          {/* DATE */}
          <DateField
            label={t(($) => $.terminate.meterDetails.fields.date)}
            placeholder={t(($) => $.add.steps.meterDetails.form.contractStartDate.placeholder)}
            max={maxDate}
            min={minDate}
          />

          {/* DOC UPLOAD */}
          <DocumentUpload />

          {terminateData[TerminateSteps.METER_DETAILS].digitalMeter || isDateInFuture ? (
            <div className="form-group">
              <strong className={classNames(styles['fieldset-legend'], 'mb-none')}>{t(($) => $.fields.meterReadings.label)}</strong>
              <div className={styles.description}>
                {t(
                  ($) =>
                    $.fields.meterReadings[
                      terminateData[TerminateSteps.METER_DETAILS].digitalMeter ? 'messageDigitalMeter' : 'messageDateInFuture'
                    ]
                )}
              </div>
            </div>
          ) : (
            <IndexesFormsInputs
              {...{ explicitRequired: !isDateInFuture, serviceContracts }}
              setHigherCheck={(result) => setHasVeryHighUsage(result)}
              setLowerCheck={(result) => setHasVeryLowUsage(result)}
              showEan
              showLastMeterReadingByDgo
            />
          )}
          <FormButtons currentStep={TerminateSteps.METER_DETAILS} trackingId={ContractFlowTrackingTypes.TERMINATE} />
        </Form>
      </FormProvider>

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        setClose={() => setIsConfirmationModalOpen(false)}
        onSubmit={() => setNextStep(getValues(), TerminateSteps.REFERRAL)}
        state={confirmationState}
      />
    </section>
  )
}

export default MeterDetails
